import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  UserCheck, 
  CheckCircle2, 
  Phone,
  ArrowLeft,
  ArrowRight,
  Eye,
  Zap,
  DollarSign,
  RefreshCw,
  Target,
  Award
} from "lucide-react";
import { LucideIcon } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  Eye,
  Award,
  DollarSign,
  RefreshCw,
  Zap,
  Target
};

export default function ExternalConsulting() {
  const { t, i18n } = useTranslation('services-pages');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const benefitsRaw = t('externalConsulting.benefits', { returnObjects: true });
  const benefits = Array.isArray(benefitsRaw) ? benefitsRaw : (benefitsRaw as any)?.items || [];

  const comparisonRaw = t('externalConsulting.comparison', { returnObjects: true });
  const comparisonData = Array.isArray(comparisonRaw) ? comparisonRaw : (comparisonRaw as any)?.rows || [];

  const packagesRaw = t('externalConsulting.packages', { returnObjects: true });
  const servicePackages = Array.isArray(packagesRaw) ? packagesRaw : (packagesRaw as any)?.items || [];

  const faqsRaw = t('externalConsulting.faqs', { returnObjects: true });
  const faqs = Array.isArray(faqsRaw) ? faqsRaw : (faqsRaw as any)?.items || [];

  return (
    <Layout>
      <Helmet>
        <title>{t('externalConsulting.meta.title')}</title>
        <meta name="description" content={t('externalConsulting.meta.description')} />
        <link rel="canonical" href="https://adam-safety.com/services/external-consulting" />
        <meta property="og:title" content={t('externalConsulting.meta.title')} />
        <meta property="og:description" content={t('externalConsulting.meta.description')} />
        <meta property="og:url" content="https://adam-safety.com/services/external-consulting" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('externalConsulting.meta.title')} />
        <meta name="twitter:description" content={t('externalConsulting.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <UserCheck className="w-5 h-5" />
              <span className="text-sm font-medium">{t('externalConsulting.hero.badge')}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {t('externalConsulting.hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('externalConsulting.hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {t('externalConsulting.hero.cta')}
                </Button>
              </Link>
              <Link to="/services/safety-officer">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  {t('externalConsulting.hero.secondaryCta')}
                  <ArrowIcon className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('externalConsulting.benefits.title', t('externalConsulting.benefitsTitle'))}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('externalConsulting.benefits.subtitle', t('externalConsulting.benefitsSubtitle'))}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon] || Eye;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-service"
                >
                  <div className="icon-container mb-4">
                    <IconComponent className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('externalConsulting.comparison.title', t('externalConsulting.comparisonTitle'))}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto overflow-x-auto"
          >
            <table className="w-full bg-card rounded-2xl overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-primary text-primary-foreground">
                  <th className="px-6 py-4 text-right font-bold">{t('externalConsulting.comparison.headers.aspect', t('externalConsulting.table.aspect'))}</th>
                  <th className="px-6 py-4 text-right font-bold">{t('externalConsulting.comparison.headers.internal', t('externalConsulting.table.internal'))}</th>
                  <th className="px-6 py-4 text-right font-bold">{t('externalConsulting.comparison.headers.external', t('externalConsulting.table.external'))}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-card' : 'bg-secondary/30'}>
                    <td className="px-6 py-4 font-medium text-foreground">{row.aspect}</td>
                    <td className="px-6 py-4 text-muted-foreground">{row.internal}</td>
                    <td className="px-6 py-4 text-accent font-medium">{row.external}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Service Packages */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('externalConsulting.packages.title', t('externalConsulting.packagesTitle'))}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('externalConsulting.packages.subtitle', t('externalConsulting.packagesSubtitle'))}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {servicePackages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-3xl p-8 ${
                  pkg.highlighted 
                    ? 'bg-primary text-primary-foreground ring-4 ring-accent' 
                    : 'bg-card border border-border'
                }`}
              >
                <h3 className={`text-2xl font-bold mb-2 ${pkg.highlighted ? '' : 'text-foreground'}`}>
                  {pkg.title}
                </h3>
                <p className={`mb-6 ${pkg.highlighted ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                  {pkg.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        pkg.highlighted ? 'text-accent' : 'text-accent'
                      }`} />
                      <span className={pkg.highlighted ? 'text-primary-foreground/90' : 'text-muted-foreground'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button 
                    variant={pkg.highlighted ? "cta" : "outline"} 
                    className="w-full"
                  >
                    {t('externalConsulting.requestQuote')}
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('externalConsulting.faqs.title', t('externalConsulting.faqTitle'))}
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {t('externalConsulting.cta.title')}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              {t('externalConsulting.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {t('externalConsulting.cta.button')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
